/** Reusable architectural dressing for Arena interiors. */
import * as pc from 'playcanvas'
import type { AABB } from '../colliders'
import { createRenderEntity, type RenderPrimitive } from './interiorShared'

type Vec3 = readonly [number, number, number]

export const INTERIOR_GLASS_ROOM_HEIGHT = 2.65
export const INTERIOR_GLASS_DOOR_HALF_WIDTH = 0.75

export interface GlassRoomPanelLayout {
  suffix: string
  position: Vec3
  scale: Vec3
  collider: AABB
}

/** One shared geometry contract for rendered glass and traversal fixtures. */
export function glassRoomPanelLayout(
  cx: number,
  cz: number,
  width: number,
  depth: number,
): readonly GlassRoomPanelLayout[] {
  const height = INTERIOR_GLASS_ROOM_HEIGHT
  const panelY = height / 2
  const halfW = width / 2
  const halfD = depth / 2
  const frontSegment = halfW - INTERIOR_GLASS_DOOR_HALF_WIDTH
  const panels: Array<Omit<GlassRoomPanelLayout, 'collider'>> = [
    { suffix: 'glass-back', position: [cx, panelY, cz - halfD], scale: [width, height, 0.06] },
    { suffix: 'glass-west', position: [cx - halfW, panelY, cz], scale: [0.06, height, depth] },
    { suffix: 'glass-east', position: [cx + halfW, panelY, cz], scale: [0.06, height, depth] },
    {
      suffix: 'glass-front-west',
      position: [
        cx - INTERIOR_GLASS_DOOR_HALF_WIDTH - frontSegment / 2,
        panelY,
        cz + halfD,
      ],
      scale: [frontSegment, height, 0.06],
    },
    {
      suffix: 'glass-front-east',
      position: [
        cx + INTERIOR_GLASS_DOOR_HALF_WIDTH + frontSegment / 2,
        panelY,
        cz + halfD,
      ],
      scale: [frontSegment, height, 0.06],
    },
  ]
  return panels.map((panel) => ({
    ...panel,
    collider: {
      minX: panel.position[0] - panel.scale[0] / 2,
      maxX: panel.position[0] + panel.scale[0] / 2,
      minY: 0,
      maxY: height,
      minZ: panel.position[2] - panel.scale[2] / 2,
      maxZ: panel.position[2] + panel.scale[2] / 2,
      blocksSight: false,
    },
  }))
}

export function createInteriorMaterial(
  color: Vec3,
  options: {
    emissive?: number
    opacity?: number
    metalness?: number
    gloss?: number
  } = {},
): pc.StandardMaterial {
  const value = new pc.StandardMaterial()
  value.diffuse = new pc.Color(...color)
  value.gloss = options.gloss ?? 0.5
  if (options.metalness !== undefined) {
    value.useMetalness = true
    value.metalness = options.metalness
  }
  if (options.emissive) {
    value.emissive = new pc.Color(...color)
    value.emissiveIntensity = options.emissive
  }
  if (options.opacity !== undefined) {
    value.opacity = options.opacity
    value.blendType = pc.BLEND_NORMAL
    value.depthWrite = false
    value.cull = pc.CULLFACE_NONE
  }
  value.update()
  return value
}

/**
 * Small authored assemblies shared by every room. Glass partitions register
 * colliders; loose furniture stays render-only so circulation remains owned by
 * each room's tested layout contract.
 */
export class InteriorDressingKit {
  constructor(private readonly root: pc.Entity) {}

  private add(
    name: string,
    type: RenderPrimitive,
    position: Vec3,
    scale: Vec3,
    material: pc.Material,
    parent = this.root,
    collider?: AABB,
  ): pc.Entity {
    return createRenderEntity({ name, type, position, scale, material, parent, collider })
  }

  addChair(
    name: string,
    x: number,
    z: number,
    rotationY: number,
    upholstery: pc.Material,
    frame: pc.Material,
  ): void {
    const chair = new pc.Entity(name)
    chair.setLocalPosition(x, 0, z)
    chair.setLocalEulerAngles(0, rotationY, 0)
    this.root.addChild(chair)
    this.add(`${name}-seat`, 'box', [0, 0.48, 0], [0.58, 0.12, 0.58], upholstery, chair)
    this.add(`${name}-back`, 'box', [0, 0.86, 0.25], [0.58, 0.68, 0.10], upholstery, chair)
    this.add(`${name}-base`, 'cylinder', [0, 0.23, 0], [0.10, 0.46, 0.10], frame, chair)
    this.add(`${name}-foot`, 'cylinder', [0, 0.04, 0], [0.46, 0.08, 0.46], frame, chair)
  }

  addPlanter(
    name: string,
    x: number,
    z: number,
    pot: pc.Material,
    foliage: pc.Material,
  ): void {
    this.add(`${name}-pot`, 'cylinder', [x, 0.34, z], [0.62, 0.68, 0.62], pot)
    this.add(`${name}-foliage-low`, 'sphere', [x, 0.88, z], [0.95, 0.80, 0.95], foliage)
    this.add(`${name}-foliage-high`, 'sphere', [x + 0.18, 1.35, z - 0.08], [0.58, 0.82, 0.58], foliage)
  }

  addCeilingRaft(
    name: string,
    x: number,
    z: number,
    width: number,
    depth: number,
    ceilingY: number,
    frame: pc.Material,
    glow: pc.Material,
  ): void {
    this.add(`${name}-raft`, 'box', [x, ceilingY - 0.18, z], [width, 0.12, depth], frame)
    this.add(`${name}-light-west`, 'box', [x - width * 0.28, ceilingY - 0.25, z], [0.10, 0.04, depth * 0.72], glow)
    this.add(`${name}-light-east`, 'box', [x + width * 0.28, ceilingY - 0.25, z], [0.10, 0.04, depth * 0.72], glow)
  }

  addGlassRoom(
    name: string,
    cx: number,
    cz: number,
    width: number,
    depth: number,
    glass: pc.Material,
    frame: pc.Material,
  ): void {
    const height = INTERIOR_GLASS_ROOM_HEIGHT
    const panelY = height / 2
    const halfW = width / 2
    const halfD = depth / 2
    const frameT = 0.10
    for (const panel of glassRoomPanelLayout(cx, cz, width, depth)) {
      this.add(
        `${name}-${panel.suffix}`,
        'box',
        panel.position,
        panel.scale,
        glass,
        this.root,
        panel.collider,
      )
    }
    for (const [index, x] of [
      cx - halfW,
      cx + halfW,
      cx - INTERIOR_GLASS_DOOR_HALF_WIDTH,
      cx + INTERIOR_GLASS_DOOR_HALF_WIDTH,
    ].entries()) {
      this.add(`${name}-frame-${index}`, 'box', [x, panelY, cz + halfD], [frameT, height, frameT], frame)
    }
    this.add(`${name}-frame-back`, 'box', [cx, height - frameT / 2, cz - halfD], [width, frameT, frameT], frame)
    this.add(`${name}-frame-front`, 'box', [cx, height - frameT / 2, cz + halfD], [width, frameT, frameT], frame)
  }

  addWorkstation(
    name: string,
    x: number,
    z: number,
    rotationY: number,
    surface: pc.Material,
    frame: pc.Material,
    screen: pc.Material,
    upholstery: pc.Material,
  ): void {
    const pod = new pc.Entity(name)
    pod.setLocalPosition(x, 0, z)
    pod.setLocalEulerAngles(0, rotationY, 0)
    this.root.addChild(pod)
    this.add(`${name}-top`, 'box', [0, 0.76, 0], [2.5, 0.12, 0.9], surface, pod)
    for (const dx of [-1.05, 1.05]) {
      this.add(`${name}-leg-${dx}`, 'box', [dx, 0.38, 0], [0.10, 0.76, 0.68], frame, pod)
    }
    this.add(`${name}-screen`, 'box', [0, 1.22, -0.30], [1.15, 0.68, 0.06], screen, pod)
    this.addChair(`${name}-chair`, x, z + 1.05, rotationY, upholstery, frame)
  }
}
